// Webpostman gönderi listesi (`/cargo`) ve hareket dökümü (`/movements`) yanıtlarını OPOP'un gönderi
// kaydına çeviren SAF fonksiyonlar (ağ çağrısı yok). FEST/Deposel/Soyalp aynı Webpostman altyapısını
// kullandığı için alan adları ortaktır; cariye özgü olan tek şey şube kodlarıdır (bkz. channelFromBranchName).
//
// Alanlar canlı Deposel yanıtından doğrulandı (2026-10-03): `/cargo` satırı gonderino, musteribarkod,
// cikisno, tedbarkod, durum (kova 0-6), statu_no (tek/iki haneli), alt_statu, sonuc, onsonuc, sube,
// subekod, subetel, sehiradi, ilce, tutar ("599,00"), odeme_turu, alimtarihi, degisimTarih
// ("2026-10-03 14:25:37", İstanbul saati), sonuctarihi, teslimtarihi, odemetarihi, tahsiltarihi, url.
import { stripLeadingZeros } from './barcode'
import { normalizeFestStatusCode } from './types'

/* eslint-disable @typescript-eslint/no-explicit-any */

/** `/cargo` satırından çıkarılan, saklanacak gönderi anlık görüntüsü. */
export interface CargoShipmentSnapshot {
  /** Webpostman iç gönderi no (har_kod) — cari içinde benzersiz anahtar. */
  gonderino: string
  /** Bizim gönderdiğimiz barkod (sıfırsız). Siparişe bu anahtarla bağlanır; bilinmiyorsa null. */
  barcode: string | null
  /** Taşıyıcı (Aras/PTT) takip numarası. */
  trackingNo: string | null
  sipno: string | null
  /** Durum kovası: 0 Kabul, 1 Teslim, 2 İade, 3 Haber Formu, 4 Normal, 5 Kayıp, 6 Sorunlu. */
  durum: number | null
  /** İnce statü kodu, iki haneli ("01", "40", "10"...). */
  statuNo: string | null
  altStatu: string | null
  /** Son sonuç metni (20 karakterde kesik gelebilir). */
  sonuc: string | null
  /** Bulunduğu şube/DTM ya da iade işareti ("Ş.İADE", "M.İADE"); bazen randevu tarihi. */
  onSonuc: string | null
  /** Aracı hub adı (gerçek konum DEĞİL). */
  sube: string | null
  /** Gönderimde seçilen dağıtıcı şube kodu — cariye özgüdür (YETREK AS1=Aras, FIDANS 663=Aras...). */
  subeKod: string | null
  subeTel: string | null
  il: string | null
  ilce: string | null
  /** Tahsilat tutarı (TL). */
  tutar: number | null
  /** Ödeme türü metni. DİKKAT: teslimden sonra FİİLİ tahsilata döner (KV ile gidip nakit ödenirse
   *  "Peşin Ödeme" olur) → teslim oranını bu alana göre hesaplamak döngüseldir. */
  odemeTuru: string | null
  /** Kargo kabul (alım) tarihi, YYYY-MM-DD. */
  alimTarihi: string | null
  /** Son hareket zamanı (ISO, +03:00). */
  degisimAt: string | null
  sonucTarihi: string | null
  teslimTarihi: string | null
  odemeTarihi: string | null
  tahsilTarihi: string | null
  url: string | null
}

const str = (v: unknown): string | null => {
  if (v == null) return null
  const s = String(v).trim()
  return s === '' ? null : s
}

/** "1.234,50" / "599,00" / "599.5" → sayı. Boş/geçersizse null. */
export function parseTrAmount(v: unknown): number | null {
  const s = str(v)
  if (!s) return null
  const normalized = s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s
  const n = Number(normalized)
  return Number.isFinite(n) ? n : null
}

/** "YYYY-MM-DD…" → "YYYY-MM-DD"; boş, "0000-00-00" ya da geçersizse null. */
export function parseApiDate(v: unknown): string | null {
  const s = str(v)
  if (!s || !/^\d{4}-\d{2}-\d{2}/.test(s) || s.startsWith('0000')) return null
  return s.slice(0, 10)
}

/** İstanbul yerel zamanı ("2026-10-03 14:25:37" ya da yalnız tarih) → ISO (+03:00). Türkiye 2016'dan
 *  beri yaz saati uygulamadığı için sabit +03:00 doğrudur. */
export function istanbulLocalToIso(v: unknown): string | null {
  const s = str(v)
  if (!s) return null
  const m = s.match(/^(\d{4}-\d{2}-\d{2})(?:[ T](\d{2}:\d{2})(?::(\d{2}))?)?/)
  if (!m || m[1].startsWith('0000')) return null
  return `${m[1]}T${m[2] ?? '00:00'}:${m[3] ?? '00'}+03:00`
}

/** Tarihi Webpostman sorgu biçimine ("GG-AA-YYYY") İstanbul takvim gününe göre çevirir. */
export function formatWebpostmanDate(d: Date): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Istanbul', day: '2-digit', month: '2-digit', year: 'numeric',
  }).formatToParts(d)
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? ''
  return `${get('day')}-${get('month')}-${get('year')}`
}

/**
 * Bizim barkodumuzu kurtarır: normalde `musteribarkod`. Bazı cariler barkodu elle girerken `sipno`
 * alanına yazar (bkz. trackingRecordFromRow); sipno gerçek sipariş numarası da olabildiğinden yalnız
 * barkod uzunluğundaysa (≥8 hane) kabul edilir — kısa sipariş no'su yanlış eşleşmesin.
 */
function recoverBarcode(row: any): string | null {
  const mb = str(row?.musteribarkod)
  if (mb) return stripLeadingZeros(mb) || null
  const sip = str(row?.sipno)
  if (sip && /^\d{8,}$/.test(sip)) return stripLeadingZeros(sip) || null
  return null
}

/** `/cargo` satırını gönderi anlık görüntüsüne çevirir; gönderi no'su yoksa null. */
export function parseListingRow(row: any): CargoShipmentSnapshot | null {
  const gonderino = str(row?.gonderino)
  if (!gonderino) return null
  const durumRaw = str(row?.durum)
  const durum = durumRaw != null && /^\d+$/.test(durumRaw) ? Number(durumRaw) : null
  const statu = str(row?.statu_no)
  return {
    gonderino,
    barcode: recoverBarcode(row),
    trackingNo: str(row?.cikisno) ?? str(row?.tedbarkod),
    sipno: str(row?.sipno),
    durum,
    statuNo: statu != null ? normalizeFestStatusCode(statu) : null,
    altStatu: str(row?.alt_statu),
    sonuc: str(row?.sonuc),
    onSonuc: str(row?.onsonuc),
    sube: str(row?.sube),
    subeKod: str(row?.subekod),
    subeTel: str(row?.subetel),
    il: str(row?.sehiradi),
    ilce: str(row?.ilce),
    tutar: parseTrAmount(row?.tutar),
    odemeTuru: str(row?.odeme_turu),
    alimTarihi: parseApiDate(row?.alimtarihi),
    degisimAt: istanbulLocalToIso(row?.degisimTarih),
    sonucTarihi: parseApiDate(row?.sonuctarihi),
    teslimTarihi: parseApiDate(row?.teslimtarihi),
    odemeTarihi: parseApiDate(row?.odemetarihi),
    tahsilTarihi: parseApiDate(row?.tahsiltarihi),
    url: str(row?.url),
  }
}

/** Ödeme türü metninin kapıda kart olup olmadığı (gösterim/rapor için; teslim oranı için KULLANMA). */
export function isCardPaymentText(odemeTuru: string | null | undefined): boolean {
  return /kredi/i.test(odemeTuru ?? '')
}

function foldTr(s: string): string {
  return s
    .toLocaleLowerCase('tr-TR')
    .replace(/̇/g, '')
    .replace(/ı/g, 'i').replace(/ş/g, 's').replace(/ğ/g, 'g')
    .replace(/ü/g, 'u').replace(/ö/g, 'o').replace(/ç/g, 'c')
}

/** Kanonik dağıtıcı kanalları. Bilinmeyen dağıtıcılar için şube adı/kodu olduğu gibi taşınır. */
export const CARGO_CHANNEL = {
  ARAS: 'ARAS',
  PTT: 'PTT',
  YURTICI: 'YURTICI',
  MNG: 'MNG',
  SURAT: 'SURAT',
  KARGOTURK: 'KARGOTURK',
} as const

/**
 * Cari şube adından ("Aras - Deposer İstanbul", "PTT KARGO-2", "ARAS KARGO") dağıtıcı kanalı çıkarır.
 * Şube kodları cariye özgü olduğundan (YETREK AS1, FIDANS 663, TEYS ARS = Aras) kanal koddan değil,
 * OPOP'taki `cargo_branches` adından türetilir. Tanınmazsa null.
 */
export function channelFromBranchName(name: string | null | undefined): string | null {
  if (!name) return null
  const f = foldTr(name)
  if (/\bptt\b/.test(f)) return CARGO_CHANNEL.PTT
  if (/\baras\b/.test(f)) return CARGO_CHANNEL.ARAS
  if (/yurt\s*ici/.test(f)) return CARGO_CHANNEL.YURTICI
  if (/\bmng\b/.test(f)) return CARGO_CHANNEL.MNG
  if (/\bsurat\b/.test(f)) return CARGO_CHANNEL.SURAT
  if (/kargo\s*turk/.test(f)) return CARGO_CHANNEL.KARGOTURK
  return null
}

/**
 * Gönderinin kanalını çözer: önce carinin şube tablosu (kod → ad), sonra aracı hub adı
 * ("ARAS ADANA" gibi doğrudan taşıyıcı şubesi dönen cariler), en son ham şube kodu.
 */
export function resolveShipmentChannel(
  snapshot: Pick<CargoShipmentSnapshot, 'subeKod' | 'sube'>,
  branchNameByCode: ReadonlyMap<string, string>,
): string | null {
  const code = snapshot.subeKod
  if (code) {
    const fromBranch = channelFromBranchName(branchNameByCode.get(code))
    if (fromBranch) return fromBranch
  }
  return channelFromBranchName(snapshot.sube) ?? code ?? null
}

/** `/movements` yanıtından çıkarılan hareket özeti. */
export interface MovementSummary {
  found: boolean
  moveCount: number
  /** "Haber Formu Bırakıldı" sayısı (kurye adrese gitti, alıcı yok). */
  haberCount: number
  teslimEdilemediCount: number
  /** Kuryenin alıcıyı araması. */
  aranmaCount: number
  smsCount: number
  gorusmeCount: number
  /** Kurye kapıya gitti mi (haber formu, arama ya da teslim hareketi var). */
  kapiyaGidildi: boolean
  /** Teslimatı yapan/yapacak şube (iade olsa da hareket dökümünde kalır). */
  varisSubesi: string | null
  kabulAt: string | null
  /** İlk haber formu — şubede bekleme/iade geri sayımının başlangıcı. */
  firstHaberAt: string | null
  iadeBasladiAt: string | null
  iadeEdildiAt: string | null
  teslimAt: string | null
  lastMoveAt: string | null
}

/** Webpostman `error` alanı boolean ya da "true"/"false" string olabilir. */
function errored(data: any): boolean {
  const e = data?.error
  if (e === true || e === 1) return true
  if (typeof e === 'string') return ['true', '1'].includes(e.trim().toLowerCase())
  return false
}

/** `/movements/{barkod}` ham yanıtını özetler. Gönderi bulunamadıysa found=false döner. */
export function summarizeMovements(data: any): MovementSummary {
  const empty: MovementSummary = {
    found: false, moveCount: 0, haberCount: 0, teslimEdilemediCount: 0, aranmaCount: 0, smsCount: 0, gorusmeCount: 0,
    kapiyaGidildi: false, varisSubesi: null, kabulAt: null, firstHaberAt: null, iadeBasladiAt: null, iadeEdildiAt: null,
    teslimAt: null, lastMoveAt: null,
  }
  if (!data || errored(data)) return empty
  const moves = (Array.isArray(data.hareketler) ? data.hareketler : [])
    .map((h: any) => ({ at: istanbulLocalToIso(h?.tarih), text: foldTr(String(h?.yapilan_islem ?? '')), bucket: str(h?.durum_no) }))
    .filter((h: { at: string | null }) => h.at)
    .sort((a: { at: string }, b: { at: string }) => a.at.localeCompare(b.at)) as { at: string; text: string; bucket: string | null }[]
  const matching = (re: RegExp) => moves.filter((m) => re.test(m.text))
  const firstAt = (re: RegExp) => matching(re)[0]?.at ?? null
  // Kova 3 = Haber Formu; metin de "Haber Formu Bırakıldı". İkisinden biri yeterli.
  const haber = moves.filter((m) => m.bucket === '3' || /haber formu/.test(m.text))
  const aranma = matching(/arand/).length
  const teslim = matching(/^teslim edildi/).length
  return {
    found: true,
    moveCount: moves.length,
    haberCount: haber.length,
    teslimEdilemediCount: matching(/teslim edilemedi/).length,
    aranmaCount: aranma,
    smsCount: matching(/\bsms\b/).length,
    gorusmeCount: Array.isArray(data.gorusme) ? data.gorusme.length : 0,
    kapiyaGidildi: haber.length > 0 || aranma > 0 || teslim > 0,
    varisSubesi: str(data.varis_subesi),
    kabulAt: firstAt(/kabul/),
    firstHaberAt: haber[0]?.at ?? null,
    iadeBasladiAt: firstAt(/iade surec/),
    iadeEdildiAt: firstAt(/iade edildi/),
    teslimAt: firstAt(/^teslim edildi/),
    lastMoveAt: moves.length ? moves[moves.length - 1].at : null,
  }
}
/* eslint-enable @typescript-eslint/no-explicit-any */
